/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { FC, useEffect, useRef, useState } from "react";

import { default as classNames, default as cn } from "classnames";
import { useLocation, useNavigate } from "react-router-dom";

import { ROUTES, extractBaseURL, getSmartRoute } from "Router";
import { useDetectClickOutside } from "utils/useDetectClickOutside";

import useDevice from "hooks/useDevice";
import { useAuth, usePrimaryEvents, useRole, useUI } from "state";

import { PrimaryEventDialog } from "components/common/PrimaryEventsMenu/PrimaryEventsMenu";
import { EventConfigIcon } from "components/icons/Sidebar";
import HomeIcon from "components/icons/Sidebar/HomeIcon";

import { useDefaultSidebarList } from "./constants";
import s from "./Sidebar.module.css";
import SidebarMenuList from "./SidebarMenuList";

type Props = {
  isOpen: boolean;
  isLoggedIn: boolean;
};

type SidebarList = {
  title: string;
  pages: Array<SidebarListPage>;
  isOpen: boolean;
  id: string;
  icon: any;
};

type SidebarListPage = {
  name: string;
  slug: string;
  feature_flag?: string;
};

const useSidebar = (initialSidebarList: SidebarList[]) => {
  const { hasSelectedEvent } = usePrimaryEvents();
  const isFirstRender = useRef(true);
  const { isLoggedIn } = useAuth();
  const { pages: PAGES_TO_ACCESS } = useRole();

  const [sidebarList, setSidebarList] = useState<SidebarList[]>(
    initialSidebarList as SidebarList[],
  );

  const filterPages = (sidebarList: SidebarList[], allowedPages: string[]) => {
    const FILTERED_GROUP_LIST = sidebarList
      .map((list) => {
        const pages = list.pages.filter(() => {
          const pageName = getSmartRoute("ROUTING");
          return allowedPages.includes(pageName);
        });

        return {
          ...list,
          pages,
        };
      })
      .filter((list) => list.pages.length > 0);

    return FILTERED_GROUP_LIST;
  };

  const checkIsRouteInsideList = (
    pages: SidebarListPage[],
    baseURL: string,
  ) => {
    const isInside = pages.some(({ slug }) => baseURL.includes(slug));
    isFirstRender.current = false;
    return isInside;
  };

  useEffect(() => {
    if (PAGES_TO_ACCESS.length > 0) {
      isFirstRender.current = true;

      setSidebarList(
        filterPages(initialSidebarList as SidebarList[], PAGES_TO_ACCESS),
      );
    }
  }, [PAGES_TO_ACCESS]);

  useEffect(() => {
    if (!isLoggedIn || !initialSidebarList) return;
    const baseURL = "/" + extractBaseURL();
    setSidebarList(
      initialSidebarList.map((list) => {
        const isInside = checkIsRouteInsideList(list.pages, baseURL);
        if (isInside) {
          return { ...list, isOpen: true };
        }

        return { ...list, isOpen: false };
      }),
    );
  }, [hasSelectedEvent, initialSidebarList]);

  return { sidebarList, setSidebarList };
};

const HIDE_DEFAULT_SIDEBAR_ROUTES: string[] = ["/general/", "/trip/"];

const Sidebar: FC<Props> = ({ isOpen, isLoggedIn }) => {
  const { hasSelectedEvent } = usePrimaryEvents();
  const { SidebarList: initialSidebarList } = useDefaultSidebarList();

  const { sidebarList: sidebarLists, setSidebarList: setSidebarLists } =
    useSidebar(initialSidebarList as SidebarList[]);

  const { isSidebarShrunk: isShrunk, setSidebarShrunk, sidebarData } = useUI();
  const { isDesktop, isMobile, isTablet } = useDevice();
  const { pathname } = useLocation();
  const sidebarRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();

  const shouldHideSidebar = HIDE_DEFAULT_SIDEBAR_ROUTES.some((route) =>
    pathname.includes(route),
  );

  useDetectClickOutside(sidebarRef, () => {
    if (isDesktop || isMobile) return;
  });

  const handleToggleMenu = (title: string) => {
    const newLists = sidebarLists.map((list) => {
      if (list.title === title) {
        return { ...list, isOpen: !list.isOpen };
      }

      return { ...list, isOpen: false };
    });

    setSidebarLists(newLists);
  };

  useEffect(() => {
    if (isTablet) setSidebarShrunk(true);
    if (isMobile) setSidebarShrunk(false);
  }, [isTablet, isMobile]);

  return (
    <aside
      ref={sidebarRef}
      className={cn(
        s.aside,
        isOpen ? s.show : s.hideInMobile,
        !isLoggedIn && s.alwaysHide, // always hide sidebar if not logged in
      )}
    >
      <div
        className={cn(
          s.sidebarContainer,
          isShrunk ? "sm:!w-[75px]" : "sm:w-[230px]",
        )}
      >
        {shouldHideSidebar ? (
          sidebarData ? (
            sidebarData
          ) : isShrunk ? null : (
            <LoadingSkeleton />
          )
        ) : (
          <main
            className={classNames(
              "!pb-32 sm:pb-0 h-full",
              isShrunk ? "overflow-visible" : "overflow-auto",
            )}
          >
            <PrimaryEventDialog />

            <div
              role="button"
              onClick={() =>
                hasSelectedEvent
                  ? navigate(ROUTES?.HOME)
                  : navigate(ROUTES.EVENTS)
              }
            >
              <div
                className={classNames(
                  "flex space-x-3.5 mx-4 px-1.5 py-4 items-center transition-colors duration-200  cursor-pointer  hover:bg-secondary border-b border-c-gray-600",
                  {
                    "justify-center": isShrunk,
                  },
                )}
              >
                {}

                <div className=" flex items-center space-x-3 w-full">
                  {hasSelectedEvent ? (
                    <HomeIcon className="!min-w-[20px] ml-[5px]" />
                  ) : (
                    <EventConfigIcon className={classNames("ml-1")} />
                  )}

                  <span
                    className={cn("text-[#9a9a9a] text-nowrap", {
                      "overflow-hidden": isShrunk,
                    })}
                  >
                    {hasSelectedEvent ? "Event Dashboard" : "Recent Events"}
                  </span>
                </div>
              </div>
            </div>

            {sidebarLists &&
              sidebarLists.length > 0 &&
              sidebarLists.map(
                ({ title, pages, isOpen, icon: Icon }, index) => (
                  <SidebarMenuList
                    index={index}
                    key={title}
                    menuDetails={{
                      title,
                      Icon,
                    }}
                    isListOpen={isOpen}
                    onMenuToggle={handleToggleMenu}
                    list={pages}
                  />
                ),
              )}
          </main>
        )}
      </div>
    </aside>
  );
};

export const LoadingSkeleton = () => {
  return (
    <svg
      className="animate-pulse"
      width="208"
      height="172"
      viewBox="0 0 208 172"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="174" height="38" transform="translate(17)" />
      <circle cx="39" cy="19" r="3" fill="#535353" />

      <rect
        x="59"
        y="16"
        width="85"
        height="6"
        rx="3"
        fill="url(#paint0_linear_4824_20124)"
      />

      <rect width="174" height="38" transform="translate(17 38)" />
      <circle cx="39" cy="57" r="3" fill="#535353" />

      <rect
        x="59"
        y="54"
        width="85"
        height="6"
        rx="3"
        fill="url(#paint1_linear_4824_20124)"
      />

      <rect width="174" height="38" transform="translate(17 76)" />
      <circle cx="39" cy="95" r="3" fill="#535353" />

      <rect
        x="59"
        y="92"
        width="85"
        height="6"
        rx="3"
        fill="url(#paint2_linear_4824_20124)"
      />

      <rect width="174" height="38" transform="translate(17 114)" />
      <circle cx="39" cy="133" r="3" fill="#535353" />

      <rect
        x="59"
        y="130"
        width="85"
        height="6"
        rx="3"
        fill="url(#paint3_linear_4824_20124)"
      />

      <defs>
        <linearGradient
          id="paint0_linear_4824_20124"
          x1="59"
          y1="16"
          x2="161.823"
          y2="16"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#CDCDCD" stop-opacity="0.27" />
          <stop offset="1" stop-color="#565656" stop-opacity="0" />
        </linearGradient>

        <linearGradient
          id="paint1_linear_4824_20124"
          x1="59"
          y1="54"
          x2="161.823"
          y2="54"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#CDCDCD" stop-opacity="0.27" />
          <stop offset="1" stop-color="#565656" stop-opacity="0" />
        </linearGradient>

        <linearGradient
          id="paint2_linear_4824_20124"
          x1="59"
          y1="92"
          x2="161.823"
          y2="92"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#CDCDCD" stop-opacity="0.27" />
          <stop offset="1" stop-color="#565656" stop-opacity="0" />
        </linearGradient>

        <linearGradient
          id="paint3_linear_4824_20124"
          x1="59"
          y1="130"
          x2="161.823"
          y2="130"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#CDCDCD" stop-opacity="0.27" />
          <stop offset="1" stop-color="#565656" stop-opacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
};

export default Sidebar;
